import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const CancelBooking = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    
    const [bookingId, setBookingId] = useState(searchParams.get('id') || '');
    const [email, setEmail] = useState(searchParams.get('email') || '');
    const [reason, setReason] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [cancellationRules, setCancellationRules] = useState(null);

    useEffect(() => {
        fetchCancellationRules();
    }, []);

    const fetchCancellationRules = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/booking/cancellation-rules`);
            setCancellationRules(response.data);
        } catch (error) {
            console.error('Error fetching cancellation rules:', error);
        }
    };

    const handleCancel = async (e) => {
        e.preventDefault();
        
        if (!bookingId || !email) {
            toast.error('Bitte Buchungs-ID und E-Mail eingeben');
            return;
        }
        
        setLoading(true);
        
        try {
            const response = await axios.post(`${API_URL}/api/booking/bookings/cancel-request`, {
                booking_id: bookingId,
                email: email,
                reason: reason || undefined
            });
            
            setResult({
                success: true,
                message: response.data.message,
                note: response.data.note,
                fee: response.data.cancellation_fee
            });
            
            toast.success('Buchung wurde storniert');
        } catch (error) {
            const errorMessage = error.response?.data?.detail || 'Fehler bei der Stornierung';
            setResult({
                success: false,
                message: errorMessage
            });
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <Navbar />
            
            <div className="pt-28 pb-16 px-4">
                <div className="max-w-md mx-auto">
                    <Card>
                        <CardContent className="p-8">
                            <h1 className="text-2xl font-bold text-center mb-2">Buchung stornieren</h1>
                            <p className="text-gray-500 text-center mb-6">
                                Geben Sie Ihre Buchungsdaten ein, um die Stornierung anzufragen.
                            </p>
                            
                            {!result ? (
                                <form onSubmit={handleCancel} className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Buchungs-ID *</label>
                                        <Input
                                            value={bookingId}
                                            onChange={(e) => setBookingId(e.target.value)}
                                            placeholder="z.B. 6952abc123..."
                                            required
                                        />
                                        <p className="text-xs text-gray-400 mt-1">
                                            Die Buchungs-ID finden Sie in Ihrer Bestätigungs-E-Mail.
                                        </p>
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-medium mb-1">E-Mail-Adresse *</label>
                                        <Input
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="ihre@email.de"
                                            required
                                        />
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Grund (optional)</label>
                                        <Textarea
                                            value={reason}
                                            onChange={(e) => setReason(e.target.value)}
                                            placeholder="Warum möchten Sie stornieren?"
                                            className="min-h-[80px]"
                                        />
                                    </div>
                                    
                                    {/* Warning with dynamic rules */}
                                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                                        <div className="flex items-start gap-2">
                                            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                            <div className="text-sm">
                                                <div className="font-semibold text-amber-800">Stornierungsbedingungen</div>
                                                {cancellationRules?.tiers?.length > 0 ? (
                                                    <ul className="text-amber-700 mt-1 space-y-1">
                                                        {[...cancellationRules.tiers]
                                                            .sort((a, b) => b.days_before - a.days_before)
                                                            .map((tier, index) => (
                                                                <li key={index}>
                                                                    • {tier.fee_percent === 0 
                                                                        ? <><strong>Mehr als {tier.days_before} Tage vor Termin:</strong> Kostenlose Stornierung</>
                                                                        : <><strong>{tier.days_before}+ Tage vor Termin:</strong> {tier.fee_percent}% Stornierungsgebühr</>
                                                                    }
                                                                </li>
                                                            ))
                                                        }
                                                        <li>
                                                            • <strong>Weniger als {Math.min(...cancellationRules.tiers.map(t => t.days_before))} Tage:</strong> {cancellationRules.default_fee_percent}% Stornierungsgebühr
                                                        </li>
                                                    </ul>
                                                ) : (
                                                    <ul className="text-amber-700 mt-1 space-y-1">
                                                        <li>• <strong>Mehr als 48h vor Termin:</strong> Kostenlose Stornierung</li>
                                                        <li>• <strong>Weniger als 48h:</strong> 100% Stornierungsgebühr</li>
                                                    </ul>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <Button 
                                        type="submit" 
                                        className="w-full bg-red-600 hover:bg-red-700"
                                        disabled={loading}
                                    >
                                        {loading ? 'Wird storniert...' : 'Buchung stornieren'}
                                    </Button>
                                    
                                    <Button 
                                        type="button"
                                        variant="outline" 
                                        className="w-full"
                                        onClick={() => navigate('/buchung')}
                                    >
                                        Abbrechen
                                    </Button>
                                </form>
                            ) : (
                                <div className="text-center">
                                    {result.success ? (
                                        <>
                                            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                                            <h2 className="text-xl font-semibold mb-2">Stornierung erfolgreich</h2>
                                            <p className="text-gray-600 mb-4">{result.message}</p>
                                            
                                            {result.note && (
                                                <div className="bg-gray-100 rounded-lg p-4 mb-4 text-left">
                                                    <p className="text-sm text-gray-700">{result.note}</p>
                                                </div>
                                            )}
                                            
                                            {result.fee > 0 && (
                                                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                                                    <p className="text-red-700 font-semibold">
                                                        Stornierungsgebühr: {result.fee.toFixed(2)}€
                                                    </p>
                                                </div>
                                            )}
                                            
                                            <p className="text-sm text-gray-500">
                                                Eine Bestätigung wurde an Ihre E-Mail-Adresse gesendet.
                                            </p>
                                        </>
                                    ) : (
                                        <>
                                            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                                            <h2 className="text-xl font-semibold mb-2">Stornierung fehlgeschlagen</h2>
                                            <p className="text-gray-600 mb-4">{result.message}</p>
                                        </>
                                    )}
                                    
                                    <Button 
                                        className="mt-6"
                                        onClick={() => navigate('/')}
                                    >
                                        Zur Startseite
                                    </Button>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
            
            <Footer />
        </div>
    );
};

export default CancelBooking;
